import * as React from 'react';
import classNames from 'classnames';
import IntersectionObserver from 'react-intersection-observer';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {NOOP} from 'components/UIKit/utilities';
import {Section, Button} from 'components/UIKit';
import {Link} from 'components/UIKit/Link';
import {Icon} from 'components/UIKit/Icon';
import * as locales from '../../locales';
import styles from './ListingTabNav.scss';

export type ListingTab = {
  id: string;
  label: string;
};

interface TabProps {
  id: string;
  label: string;
  onClick?: (tab: string) => void;
  selected?: boolean;
  noBorder?: boolean;
}

class Tab extends React.Component<TabProps> {
  handleClick = () => {
    const {onClick = NOOP, id} = this.props;
    onClick(id);
  };

  render() {
    const {id, label, selected = false, noBorder = false} = this.props;

    return (
      <li
        key={id}
        className={classNames(styles.Tab, {
          [styles.Selected]: selected,
          [styles.NoBorder]: noBorder,
        })}
      >
        <Link onClick={this.handleClick} url={`#${id}`} text={label} resetMargin />
      </li>
    );
  }
}

export interface ListingTabNavProps {
  tabs: ListingTab[];
  selected?: string;
  onTabClick?: (tab: string) => void;
  hideContact?: boolean;
  onContactClick: React.MouseEventHandler;
}

export interface ListingTabNavState {
  showSticky: boolean;
}

class ListingTabNavComponent extends React.Component<
  ListingTabNavProps & WithI18nProps,
  ListingTabNavState
> {
  state: ListingTabNavState = {
    showSticky: false,
  };

  handleArrowClick = () => {
    let scroller;

    if (document.scrollingElement) {
      scroller = document.scrollingElement;
    } else if (document.documentElement) {
      scroller = document.documentElement;
    } else {
      scroller = document.querySelector('html');
    }

    scroller.scrollTop = 0;

    location.hash = '';
  };

  handleVisibilityChange = (inView) => {
    this.setState({showSticky: !inView});
  };

  render() {
    const {
      i18n,
      tabs,
      selected,
      onTabClick = NOOP,
      hideContact = false,
      onContactClick = NOOP,
    } = this.props;

    const {showSticky} = this.state;

    return (
      <>
        <aside className={classNames(styles.FixedTabs, showSticky && styles.Showing)}>
          <nav className={styles.Nav}>
            <ul className={classNames(styles.Container, styles.Light)}>
              {tabs.map((tab) => (
                <Tab
                  key={tab.id}
                  noBorder
                  onClick={onTabClick}
                  selected={tab.id === selected}
                  {...tab}
                />
              ))}
            </ul>
            <section className={styles.Buttons}>
              {!hideContact && (
                <Button
                  type="button"
                  label={i18n.translate('contactSeller')}
                  onClick={onContactClick}
                  className={styles.Button}
                />
              )}
              <button type="button" className={styles.Arrow} onClick={this.handleArrowClick}>
                <Icon icon="arrowUp" fill="#9EAAB5" size={24} />
              </button>
            </section>
          </nav>
        </aside>
        <IntersectionObserver
          threshold={1}
          onChange={this.handleVisibilityChange}
          rootMargin="0px 0px 630px 0px"
        >
          <Section fullWidth spacing="small" resetPaddingBottom>
            <ul className={styles.Container}>
              {tabs.map((tab) => (
                <Tab key={tab.id} onClick={onTabClick} selected={tab.id === selected} {...tab} />
              ))}
            </ul>
          </Section>
        </IntersectionObserver>
      </>
    );
  }
}

export const ListingTabNav = withI18n({
  id: 'Listing',
  fallback: locales.en,
})(ListingTabNavComponent);

export default ListingTabNav;
